import { nanoid } from 'nanoid';

import type { Question } from '@api/instant_survey/utilities/types';
import { FOLDER_ID_CUSTOM, QUESTION_TYPES } from '@api/instant_survey';

/**
 * Strips ids for questions and answers when editing a recommended question or duplicating an existing question
 * @param {Object} options
 * @param {Object[]} options.availableQuestionTypes
 * @param {Boolean} [options.isDuplicate]
 * @param {Object} options.question
 * @param {Boolean} [options.shouldCreateFromOriginal]
 */
export const prepExistingQuestion = ({
  availableQuestionTypes,
  isDuplicate,
  question,
  shouldCreateFromOriginal,
}: {
  availableQuestionTypes: (typeof QUESTION_TYPES)[keyof typeof QUESTION_TYPES][];
  isDuplicate?: boolean;
  question: Question;
  shouldCreateFromOriginal?: boolean;
}) => {
  const isCreating =
    shouldCreateFromOriginal ?? (isDuplicate || question.folder !== FOLDER_ID_CUSTOM);
  const answers = question.answers.map((answer) => ({
    ...answer,
    id: isCreating ? nanoid() : answer.id,
  }));
  const subQuestions = question.subQuestions.map((subQuestion) => ({
    ...subQuestion,
    id: isCreating ? nanoid() : subQuestion.id,
  }));

  return {
    ...question,
    answers: answers.filter(
      ({ isCarryForwardGenerated, isNoneOfTheAbove }) =>
        isCarryForwardGenerated || !isNoneOfTheAbove
    ),
    id: isCreating ? undefined : question.id,
    noneAnswer:
      answers.find(
        ({ isCarryForwardGenerated, isNoneOfTheAbove }) =>
          !isCarryForwardGenerated && isNoneOfTheAbove
      ) || null,
    properties: {
      ...question.properties,
    },
    subQuestions,
    type:
      availableQuestionTypes.find(({ value }) => value === question.type)?.value ||
      question.type ||
      QUESTION_TYPES.ESSAY.value,
  };
};
